import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FiShoppingBag, FiDollarSign, FiBox, FiUsers, FiArrowUp, FiArrowRight, FiAlertTriangle, FiPackage } from 'react-icons/fi';
import { getDashboardStats } from '../../firebase/database';
import { formatPrice, formatDate, STATUS_COLORS } from '../../utils/helpers';
import { Spinner } from '../../components/common/Loader';

const StatCard = ({ title, value, icon: Icon, color, bg, change, sub, index, link }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.08 }}
    className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300 group relative overflow-hidden"
  >
    <div className={`absolute top-0 right-0 w-28 h-28 rounded-full opacity-[0.06] -translate-y-8 translate-x-8 ${bg}`} />
    <div className="flex items-start justify-between mb-4">
      <div className={`w-12 h-12 ${bg} rounded-xl flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform duration-300`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      {change && (
        <span className="flex items-center gap-1 text-green-600 text-xs font-bold bg-green-50 px-2 py-1 rounded-full">
          <FiArrowUp className="w-3 h-3" /> {change}
        </span>
      )}
    </div>
    <p className="text-3xl font-black text-gray-900 mb-1">{value}</p>
    <p className="text-gray-500 text-sm font-medium">{title}</p>
    {sub && <p className="text-gray-400 text-xs mt-0.5">{sub}</p>}
  </motion.div>
);

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { getDashboardStats().then(setStats).finally(() => setLoading(false)); }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="text-center">
        <Spinner size="lg" />
        <p className="text-gray-500 text-sm mt-3">Loading dashboard...</p>
      </div>
    </div>
  );

  const statCards = [
    { title: 'Total Revenue', value: formatPrice(stats?.totalRevenue || 0), icon: FiDollarSign, bg: 'bg-[#1a3a6e]', change: '+12%', sub: 'All time', index: 0 },
    { title: 'Total Orders', value: stats?.totalOrders || 0, icon: FiShoppingBag, bg: 'bg-[#e63946]', change: '+8%', sub: `${stats?.statusBreakdown?.pending || 0} pending`, index: 1 },
    { title: 'Products', value: stats?.totalProducts || 0, icon: FiBox, bg: 'bg-amber-500', sub: `${stats?.outOfStock || 0} out of stock`, index: 2 },
    { title: 'Customers', value: stats?.totalCustomers || 0, icon: FiUsers, bg: 'bg-emerald-500', change: '+15%', sub: `${stats?.totalAdmins || 0} admins`, index: 3 },
  ];

  const maxRevenue = Math.max(...(stats?.monthlyRevenue || []).map(m => m.revenue), 1);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-black text-gray-900">Dashboard</h1>
          <p className="text-gray-500 text-sm mt-0.5">Welcome back! Here's your store overview.</p>
        </div>
        <div className="text-sm text-gray-400">{new Date().toLocaleDateString('en-PK', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
      </div>

      {/* Alerts */}
      {(stats?.lowStockProducts > 0 || stats?.outOfStock > 0) && (
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
          className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
          <FiAlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
          <p className="text-amber-800 text-sm font-medium">
            {stats.outOfStock > 0 && <span><strong>{stats.outOfStock}</strong> products out of stock. </span>}
            {stats.lowStockProducts > 0 && <span><strong>{stats.lowStockProducts}</strong> products with low stock (&le;5).</span>}
          </p>
          <Link to="/admin/products" className="ml-auto text-amber-700 text-xs font-bold hover:underline whitespace-nowrap">Manage →</Link>
        </motion.div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map(card => <StatCard key={card.title} {...card} />)}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Revenue Chart */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
          className="lg:col-span-2 bg-white rounded-2xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-bold text-gray-900">Revenue (Last 6 Months)</h2>
          </div>
          <div className="flex items-end gap-3 h-40">
            {(stats?.monthlyRevenue || []).map((month, i) => (
              <div key={month.label} className="flex-1 flex flex-col items-center gap-1.5 group">
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: `${(month.revenue / maxRevenue) * 100}%` }}
                  transition={{ delay: i * 0.1, duration: 0.7, ease: 'easeOut' }}
                  className="w-full bg-gradient-to-t from-[#1a3a6e] to-[#2a5298] rounded-t-md relative min-h-[4px] cursor-default"
                >
                  <div className="absolute -top-8 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-10">
                    {formatPrice(month.revenue)}
                  </div>
                </motion.div>
                <span className="text-[10px] text-gray-400 font-medium">{month.label}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Order Status */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
          className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="font-bold text-gray-900 mb-5">Order Status</h2>
          <div className="space-y-3">
            {[
              { label: 'Pending', key: 'pending', color: 'bg-amber-400' },
              { label: 'Confirmed', key: 'confirmed', color: 'bg-blue-400' },
              { label: 'Shipped', key: 'shipped', color: 'bg-indigo-400' },
              { label: 'Delivered', key: 'delivered', color: 'bg-emerald-400' },
              { label: 'Cancelled', key: 'cancelled', color: 'bg-red-400' },
            ].map(({ label, key, color }) => {
              const count = stats?.statusBreakdown?.[key] || 0;
              const pct = stats?.totalOrders > 0 ? Math.round((count / stats.totalOrders) * 100) : 0;
              return (
                <div key={key}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="font-medium text-gray-700">{label}</span>
                    <span className="text-gray-500">{count} ({pct}%)</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${pct}%` }}
                      transition={{ delay: 0.5, duration: 0.8 }}
                      className={`h-full rounded-full ${color}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>
      </div>

      {/* Recent Orders */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
        className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="font-bold text-gray-900">Recent Orders</h2>
          <Link to="/admin/orders" className="text-sm text-[#1a3a6e] font-semibold hover:text-[#e63946] transition-colors flex items-center gap-1">
            View All <FiArrowRight className="w-4 h-4" />
          </Link>
        </div>
        {!stats?.recentOrders?.length ? (
          <div className="text-center py-12 text-gray-400">
            <FiPackage className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No orders yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Order ID', 'Customer', 'Items', 'Total', 'Status', 'Date'].map(h => (
                    <th key={h} className="text-left py-3 px-5 text-xs font-bold text-gray-400 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {stats.recentOrders.map((order, i) => (
                  <motion.tr key={order.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 + i * 0.05 }}
                    className="hover:bg-gray-50/80 transition-colors">
                    <td className="py-3.5 px-5 font-mono text-xs font-bold text-[#1a3a6e]">#{order.orderId || order.id?.slice(0, 8).toUpperCase()}</td>
                    <td className="py-3.5 px-5 font-medium text-gray-900">{order.customerName || 'Guest'}</td>
                    <td className="py-3.5 px-5 text-gray-500">{order.items?.length || 0}</td>
                    <td className="py-3.5 px-5 font-bold text-gray-900">{formatPrice(order.total || 0)}</td>
                    <td className="py-3.5 px-5">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${STATUS_COLORS[order.status] || 'bg-gray-100 text-gray-600'}`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="py-3.5 px-5 text-gray-400 text-xs">{formatDate(order.createdAt)}</td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default AdminDashboard;
